import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Typography,
  Descriptions,
  Button,
  Space,
  Tag,
  message,
  Form,
  Input,
  Row,
  Col,
  Card,
  Image,
  Divider,
  Alert,
  Spin,
  Modal,
} from 'antd';
import {
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  DownloadOutlined,
  DollarOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { markCashReady, markCashCollected } from '../../services/transactionService';
import { 
  sendCashReadyNotification, 
  sendCashCollectedNotification, 
  sendAdminCashPickupNotification 
} from '../../services/emailService';

const { Title, Text } = Typography;
const { TextArea } = Input;

const TransactionDocumentDrawer = ({
  visible,
  transaction,
  onClose,
  onDocumentApproval,
  onTransactionApproval,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [documentStatuses, setDocumentStatuses] = useState({});
  const [cashActionLoading, setCashActionLoading] = useState(false);

  // Get required documents based on transaction type
  const getRequiredDocuments = (transactionType) => {
    const documentConfig = {
      PTA: [
        { key: 'intl_passport_url', label: 'International Passport', required: true },
        { key: 'visa_url', label: 'Visa', required: true },
        { key: 'airline_return_ticket_url', label: 'Airline Return Ticket', required: true },
      ],
      BTA: [
        { key: 'intl_passport_url', label: 'International Passport', required: true },
        { key: 'visa_url', label: 'Visa', required: true },
        { key: 'airline_return_ticket_url', label: 'Airline Return Ticket', required: true },
        { key: 'request_letter_url', label: 'Request Letter', required: true },
        { key: 'business_reg_certificate_url', label: 'Business Registration Certificate', required: true },
        { key: 'business_cac_or_status_report', label: 'Business CAC or Status Report', required: true },
      ],
      MEDICAL_BILLS: [
        { key: 'intl_passport_url', label: 'International Passport', required: true },
        { key: 'visa_url', label: 'Visa', required: true },
        { key: 'airline_ticket_url', label: 'Airline Ticket', required: true },
        { key: 'medical_ref_letter_url', label: 'Medical Reference Letter', required: true },
        { key: 'medical_cost_evidence_url', label: 'Medical Cost Evidence', required: true },
      ],
      SCHOOL_FEES: [
        { key: 'admission_evidence_url', label: 'Admission Evidence', required: true },
        { key: 'school_invoice_url', label: 'School Invoice', required: true },
      ],
      PROFESSIONAL: [
        { key: 'registration_evidence_url', label: 'Registration Evidence', required: true },
        { key: 'invoice_url', label: 'Invoice', required: true },
      ],
      OPT_SUPPORTING_DATA: [
        { key: 'fx_source', label: 'FX Source', required: false },
        { key: 'optional_doc_url', label: 'Optional Document', required: false },
      ],
    };

    return documentConfig[transactionType] || [];
  };

  const requiredDocuments = getRequiredDocuments(transaction?.type);

  // Handle document approval/decline
  const handleDocumentAction = async (documentKey, action, reason = '') => {
    setLoading(true);
    try {
      // TODO: Implement API call to update document status
      const newStatus = action === 'approve' ? 'approved' : 'declined';
      
      setDocumentStatuses(prev => ({
        ...prev,
        [documentKey]: { status: newStatus, reason, timestamp: Date.now() }
      }));

      message.success(`Document ${action === 'approve' ? 'approved' : 'declined'} successfully`);
      
      // Call parent callback
      if (onDocumentApproval) {
        onDocumentApproval(documentKey, newStatus, reason);
      }
    } catch (error) {
      message.error(`Failed to ${action} document`);
      console.error('Document action error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check if all required documents are approved
  const allDocumentsApproved = requiredDocuments.every(doc => 
    !doc.required || documentStatuses[doc.key]?.status === 'approved'
  );

  // Handle transaction approval/decline
  const handleTransactionAction = async (action, reason = '') => {
    if (!allDocumentsApproved) {
      message.warning('Please approve all required documents first');
      return;
    }

    setLoading(true);
    try {
      // TODO: Implement API call to update transaction status
      message.success(`Transaction ${action === 'approve' ? 'approved' : 'declined'} successfully`);
      
      if (onTransactionApproval) {
        onTransactionApproval(action, reason);
      }
      
      onClose();
    } catch (error) {
      message.error(`Failed to ${action} transaction`);
      console.error('Transaction action error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle marking cash as ready for pickup
  const handleMarkCashReady = async () => {
    setCashActionLoading(true);
    try {
      await markCashReady(transaction.id);
      message.success('Cash marked as ready for pickup successfully');
      
      // Send notifications
      try {
        // Send notification to customer
        await sendCashReadyNotification(transaction.user_email, transaction);
        
        // Send notification to admin (you can get admin email from context or props)
        const adminEmail = 'admin@kadavra.com'; // This should come from user context
        await sendAdminCashPickupNotification(adminEmail, transaction, 'ready');
      } catch (notificationError) {
        console.error('Notification error:', notificationError);
        // Don't fail the main operation if notifications fail
      }
      
      // Update transaction status locally without calling onTransactionApproval
      // to avoid duplicate API calls
      if (onTransactionApproval) {
        // Pass a special flag to indicate this is a cash pickup action
        onTransactionApproval('cash-ready-success');
      }
    } catch (error) {
      // Show user-friendly error message
      let errorMessage = 'Failed to mark cash as ready for pickup';
      
      if (error.response?.data?.message) {
        const apiMessage = error.response.data.message;
        if (apiMessage.includes('disbursement_code is required')) {
          errorMessage = 'Please input the disbursement code';
        } else if (apiMessage.includes('Invalid status')) {
          errorMessage = 'Transaction status is not valid for this action';
        } else {
          errorMessage = apiMessage;
        }
      }
      
      message.error(errorMessage);
      console.error('Mark cash ready error:', error);
    } finally {
      setCashActionLoading(false);
    }
  };

  // Handle marking cash as collected
  const handleMarkCashCollected = async (disbursementCode) => {
    setCashActionLoading(true);
    try {
      await markCashCollected(transaction.id, disbursementCode);
      message.success('Cash marked as collected successfully');
      
      // Send notifications
      try {
        // Send notification to customer
        await sendCashCollectedNotification(transaction.user_email, transaction);
        
        // Send notification to admin
        const adminEmail = 'admin@kadavra.com'; // This should come from user context
        await sendAdminCashPickupNotification(adminEmail, transaction, 'collected');
      } catch (notificationError) {
        console.error('Notification error:', notificationError);
        // Don't fail the main operation if notifications fail
      }
      
      // Update transaction status locally without calling onTransactionApproval
      // to avoid duplicate API calls
      if (onTransactionApproval) {
        // Pass a special flag to indicate this is a cash pickup action
        onTransactionApproval('cash-collected-success');
      }
    } catch (error) {
      // Show user-friendly error message
      let errorMessage = 'Failed to mark cash as collected';
      
      if (error.response?.data?.message) {
        const apiMessage = error.response.data.message;
        if (apiMessage.includes('disbursement_code is required')) {
          errorMessage = 'Please input the disbursement code';
        } else if (apiMessage.includes('Invalid status')) {
          errorMessage = 'Transaction status is not valid for this action';
        } else {
          errorMessage = apiMessage;
        }
      }
      
      message.error(errorMessage);
      console.error('Mark cash collected error:', error);
    } finally {
      setCashActionLoading(false);
    }
  };

  // Check if transaction is cash type
  const isCashTransaction = transaction?.disburse_type === 'cash';

  // Render document item
  const renderDocumentItem = (document) => {
    const { key, label, required } = document;
    const documentStatus = documentStatuses[key];
    const documentUrl = transaction?.supporting_documents?.[key];
    
    return (
      <Card 
        key={key} 
        size="small" 
        style={{ marginBottom: 16 }}
        title={
          <Space>
            <FileTextOutlined />
            <Text strong>{label}</Text>
            {required && <Tag color="red">Required</Tag>}
          </Space>
        }
        extra={
          documentStatus ? (
            <Tag color={documentStatus.status === 'approved' ? 'green' : 'red'}>
              {documentStatus.status === 'approved' ? 'Approved' : 'Declined'}
            </Tag>
          ) : (
            <Tag color="default">Pending</Tag>
          )
        }
      >
        <Row gutter={16} align="middle">
          <Col span={16}>
            {documentUrl ? (
              <Space>
                <Button 
                  type="link" 
                  icon={<EyeOutlined />}
                  onClick={() => window.open(documentUrl, '_blank')}
                >
                  View Document
                </Button>
                <Button 
                  type="link" 
                  icon={<DownloadOutlined />}
                  onClick={() => window.open(documentUrl, '_blank')}
                >
                  Download
                </Button>
              </Space>
            ) : (
              <Text type="secondary">No document uploaded</Text>
            )}
          </Col>
          <Col span={8} style={{ textAlign: 'right' }}>
            <Space>
              <Button
                size="small"
                type="primary"
                onClick={() => handleDocumentAction(key, 'approve')}
                disabled={documentStatus?.status === 'approved'}
              >
                Approve
              </Button>
              <Button
                size="small"
                danger
                onClick={() => {
                  Modal.confirm({
                    title: 'Decline Document',
                    content: (
                      <Form layout="vertical">
                        <Form.Item
                          name="documentDeclineReason"
                          label="Reason"
                          rules={[{ required: true, message: 'Please provide a reason' }]}
                        >
                          <TextArea rows={3} placeholder="Why is this document being declined?" />
                        </Form.Item>
                      </Form>
                    ),
                    onOk: async () => {
                      const values = await form.validateFields();
                      handleDocumentAction(key, 'decline', values.documentDeclineReason);
                    },
                  });
                }}
                disabled={documentStatus?.status === 'declined'}
              >
                Decline
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>
    );
  };

  // Get appropriate title and description based on transaction status
  const getDrawerTitle = () => {
    if (transaction?.status === 'pending-disburse' && isCashTransaction) {
      return {
        title: 'Cash Collection',
        description: 'Mark cash as collected when customer provides disbursement code'
      };
    } else if (transaction?.status === 'approved' && isCashTransaction) {
      return {
        title: 'Cash Pickup Management',
        description: 'Mark cash as ready for pickup when funds are available'
      };
    } else {
      return {
        title: 'Transaction Document Review',
        description: 'Review documents before approving transaction'
      };
    }
  };

  const { title, description } = getDrawerTitle();

  return (
    <Drawer
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>{title}</Title>
          <Text type="secondary">{description}</Text>
        </Space>
      }
      width={800}
      open={visible}
      onClose={onClose}
      extra={
        <Space>
          <Button onClick={onClose}>Close</Button>
        </Space>
      }
    >
      {transaction ? (
        <div>
          {/* Transaction Details */}
          <Card size="small" style={{ marginBottom: 24 }}>
            <Title level={5}>Transaction Information</Title>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Reference">{transaction.reference}</Descriptions.Item>
              <Descriptions.Item label="Type">{transaction.type}</Descriptions.Item>
              <Descriptions.Item label="Amount">
                {formatCurrency(transaction.source_amount, transaction.source_currency_code)}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color="orange">{transaction.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Customer">{transaction.user_email}</Descriptions.Item>
              <Descriptions.Item label="Created">
                {formatDate(transaction.created)}
              </Descriptions.Item>
              {isCashTransaction && (
                <Descriptions.Item label="Disbursement Type">
                  <Tag color="blue">Cash Pickup</Tag>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* Document Review Section - Only show for transactions that need document review */}
          {(transaction.status === 'pending-approval' || transaction.status === 'approved') && (
            <Card size="small" style={{ marginBottom: 24 }}>
              <Title level={5}>Required Documents</Title>
              <Text type="secondary">
                Review and approve all required documents before approving the transaction
              </Text>
              
              <Divider />
              
              {requiredDocuments.map(renderDocumentItem)}
            </Card>
          )}

          {/* Transaction Approval Section - Only show for pending-approval transactions */}
          {allDocumentsApproved && transaction.status === 'pending-approval' && (
            <Card size="small" style={{ marginBottom: 24 }}>
              <Alert
                message="All Required Documents Approved"
                description="You can now approve or decline the transaction"
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
              />
              
              <Space>
                <Button
                  type="primary"
                  size="large"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleTransactionAction('approve')}
                  loading={loading}
                >
                  Approve Transaction
                </Button>
                <Button
                  danger
                  size="large"
                  icon={<CloseCircleOutlined />}
                  onClick={() => {
                    Modal.confirm({
                      title: 'Decline Transaction',
                      content: (
                        <Form form={form} layout="vertical">
                          <Form.Item
                            name="transactionDeclineReason"
                            label="Reason"
                            rules={[{ required: true, message: 'Please provide a reason' }]}
                          >
                            <TextArea rows={3} placeholder="Why is this transaction being declined?" />
                          </Form.Item>
                        </Form>
                      ),
                      onOk: async () => {
                        const values = await form.validateFields();
                        handleTransactionAction('decline', values.transactionDeclineReason);
                      },
                    });
                  }}
                >
                  Decline Transaction
                </Button>
              </Space>
            </Card>
          )}

          {/* Cash Pickup Actions */}
          {isCashTransaction && transaction.status === 'approved' && (
            <Card size="small" style={{ marginBottom: 24 }}>
              <Alert
                message="Cash Transaction Ready for Pickup"
                description="Mark cash as ready for pickup when the funds are available at the pickup location"
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              
              <Button
                type="primary"
                size="large"
                icon={<DollarOutlined />}
                onClick={handleMarkCashReady}
                loading={cashActionLoading}
              >
                Mark Cash Ready for Pickup
              </Button>
            </Card>
          )}

          {/* Cash Collection */}
          {isCashTransaction && transaction.status === 'pending-disburse' && (
            <Card size="small" style={{ marginBottom: 24 }}>
              <Alert
                message="Cash Ready for Collection"
                description="Mark cash as collected when the customer provides the disbursement code"
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
              />
              
              <Form form={form} layout="vertical">
                <Form.Item
                  name="disbursementCode"
                  label="Disbursement Code"
                  rules={[{ required: true, message: 'Please enter the disbursement code' }]}
                >
                  <Input placeholder="Enter the disbursement code provided by the customer" />
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    size="large"
                    icon={<CheckOutlined />}
                    onClick={async () => {
                      const values = await form.validateFields();
                      handleMarkCashCollected(values.disbursementCode);
                    }}
                    loading={cashActionLoading}
                  >
                    Mark Cash as Collected
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          )}

          {!allDocumentsApproved && (
            <Alert
              message="Documents Pending Review"
              description="Please review and approve all required documents before proceeding with transaction approval"
              type="warning"
              showIcon
            />
          )}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      )}
    </Drawer>
  );
};

export default TransactionDocumentDrawer;
